import { useNotification } from 'admin';
import {
  type DocumentType,
  type ErrorType,
  getApiDocumentsV1DocumentsRetrieveQueryKey,
  useApiDocumentsV1DocumentsPartialUpdate,
  useApiDocumentsV1DocumentsUploadCreate,
  useQueryClient,
  useUploadFile,
} from 'api';

export const useUploadDocument = (documentId: number) => {
  const queryClient = useQueryClient();
  const { open } = useNotification();

  const { mutateAsync: generatePreSignedURL, isPending: isGeneratingUrl } =
    useApiDocumentsV1DocumentsUploadCreate<ErrorType<Error>>();
  const { mutateAsync: uploadFile, isPending: isUploading } = useUploadFile();
  const { mutateAsync: updateDocument, isPending: isUpdating } =
    useApiDocumentsV1DocumentsPartialUpdate<ErrorType<Error>>();

  const upload = async (file: File, documentType: DocumentType) => {
    try {
      const { upload_url, file_path } = await generatePreSignedURL({
        data: { content_type: file.type },
        id: documentId,
      });

      await uploadFile({
        file,
        headers: { 'content-type': file.type },
        url: upload_url,
      });

      await updateDocument({
        data: {
          document_type: documentType,
          file_path,
        },
        id: documentId,
      });

      queryClient.invalidateQueries({
        queryKey: getApiDocumentsV1DocumentsRetrieveQueryKey(documentId),
      });
    } catch (error) {
      open?.({ message: 'Failed to upload file', type: 'error' });
      throw error;
    }
  };

  return {
    isPending: isGeneratingUrl || isUploading || isUpdating,
    upload,
  };
};
